import { Expose, Type, Transform, Exclude } from 'class-transformer';
import { DocumentSnapshot } from 'firebase/firestore';
import { _FirebaseDocument, anyToDate } from './Models';
import { useProjectsStore } from '@/stores/projects';

export type WidgetType = 'assessment_score' | 'mac_score' | 'document_count' | 'regulation_watch'

export enum Progression {
    STABLE = 'stable',
    INCREASING = 'increasing',
    DECREASING = 'decreasing',
}

export class ScoreDistribution {
    @Expose()
    public a: number = 0
    @Expose()
    public b: number = 0
    @Expose()
    public c: number = 0
    @Expose()
    public d: number = 0
    @Expose()
    public f: number = 0

    get total(): number {
        return this.a + this.b + this.c + this.d + this.f
    }

    toScore(): number {
        if (this.total === 0) return 0.0
        const WEIGHT_A = 0
        const WEIGHT_B = 2
        const WEIGHT_C = 3
        const WEIGHT_D = 4
        const WEIGHT_F = 8
        const weightedSum = (
            this.a * WEIGHT_A +
            this.b * WEIGHT_B +
            this.c * WEIGHT_C +
            this.d * WEIGHT_D +
            this.f * WEIGHT_F
        )
        let nonCompliance = weightedSum / this.total
        if (nonCompliance > 1) nonCompliance = 1
        return (1 - nonCompliance) * 100
    }
}

export class AssessmentScoreData {
    @Expose()
    public standard_id: string
    @Expose()
    public standard_name: string
    @Expose()
    public variant_id: string
    @Expose()
    public variant_name: string
    @Expose()
    public assessment_id: string
    @Expose()
    @Type(() => ScoreDistribution)
    public score_distribution: ScoreDistribution
    @Expose()
    public total_questions: number = 0
    @Expose()
    public proposed_tasks: number = 0
    @Expose()
    @Transform(anyToDate)
    public assessment_date: Date | undefined

    get weightedScore(): number {
        if (!this.total_questions) return 0
        return this.score_distribution?.toScore?.() ?? 0
    }
}

export class MACScoreData {
    @Expose()
    public score: number = 0
    @Expose()
    public progression: Progression = Progression.STABLE
    @Expose()
    @Type(() => ScoreDistribution)
    public score_distribution?: ScoreDistribution
    @Expose()
    public total_assessments: number = 0
    @Expose()
    public total_questions: number = 0
    @Expose()
    @Transform(anyToDate)
    public last_computed: Date | undefined
}

export class DocumentCountData {
    @Expose()
    public count: number = 0
    @Expose()
    @Transform(anyToDate)
    public last_computed: Date | undefined
}

export class RegulationWatchData {
    @Expose()
    public regulations_count: number = 0
    @Expose()
    public obsolete_count: number = 0
    @Expose()
    public updated_count: number = 0
    @Expose()
    @Transform(anyToDate)
    public last_report_date: Date | undefined
    // MYAC-1496 — written by a v3 report, absent (null) on a v2 one. A v3 report
    // still fills the three legacy counts for older readers; when
    // `to_review_count` is set, read these instead.
    @Expose()
    public report_id: string | null = null
    @Expose()
    public lines_count: number | null = null
    @Expose()
    public to_review_count: number | null = null
    @Expose()
    public deadline_passed_count: number | null = null
    /** Lines to review still without a position; 0 once the report is finalised. */
    @Expose()
    public remaining_count: number | null = null
}

@Exclude()
export class Widget extends _FirebaseDocument {
    @Expose()
    public type: WidgetType
    @Expose()
    public title: string
    @Expose()
    public description?: string

    @Expose()
    @Type(() => AssessmentScoreData)
    public assessment_score_data?: AssessmentScoreData | null

    @Expose()
    @Type(() => MACScoreData)
    public mac_score_data?: MACScoreData | null

    @Expose()
    @Type(() => DocumentCountData)
    public document_count_data?: DocumentCountData | null

    @Expose()
    @Type(() => RegulationWatchData)
    public regulation_watch_data?: RegulationWatchData | null

    get projectId(): string {
        return this.parent_path.split('/').pop() ?? ''
    }

    collection(): string {
        return 'widgets'
    }

    static fromFirebase(o: DocumentSnapshot): Widget | null {
        return _FirebaseDocument._fromFirebase(Widget, o)
    }
}


